import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Flip, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./manage.module.css";
import PlanScene from "./PlanScene";
import PageHeader from "../../../components/PageHeader";
import ModalForm from "../../../components/atoms/Modal";
import Otp from "../../common/Otp";
import GeneralError from "../../common/GeneralError";
import SearchLocationInput from "../../common/SearchLocationInput/SearchLocationInput";
import { getOTP, searchAwb, verifyOtpAndAction, verifyOtpActionForEdit } from "../../../utils/api";
import { getAWBSuccess, getAWBFailure } from "../../../store/actions/awb";
import { setCompanyInfo, initiateApiCall } from "../../../store/actions/companyInfo";
import { errorCode, timer, track, trackError } from "../../../analytics";
import usePageView from "../../../analytics/usePageView";
import { ensureOtpSessionFor } from "../../../utils/otpSession";
import { dataKey, draftKey, readStored, writeStored } from "../../../utils/manageOrderStore";

// Reschedule offers only these two dates (no custom date).
const DATE_KEYS = ["Tomorrow", "DayAfterTomorrow"];

// Manage Order / Cancel Order, ported from the Claude Design file "Manage Order.dc.html".
// The page reaches here after a hard reload (Edit button), so the store starts empty:
// nothing that depends on the order (above all the cancel card) renders until it loads.

const PRE_PICKUP_STATUSES = ["Awb Generated", "Shipment Created", "Pickup Failed"];
const TIME_SLOTS = ["9AM - 4PM", "4PM - 10PM"];
// Form field → tp_manage_changed / tp_manage_submit field name (null: not reported on its own).
const FIELD_NAMES = { name: "name", number: "phone", address1: "flat", address2: null, pincode: "pincode", city: null, state: null, lat: null, lng: null };
const INSTRUCTIONS = [
  ["bell", "Avoid ringing bell"],
  ["door", "Leave at the door"],
  ["guard", "Leave with security"],
  ["phone", "Avoid calling"],
];

const optionsToast = {
  position: toast.POSITION.BOTTOM_CENTER,
  transition: Flip,
  toastClassName: "toast",
  closeButton: false,
};

const cx = (...c) => c.filter(Boolean).join(" ");
const day = (offset) => moment().add(offset, "day");

const Check = ({ size = 10, width = 3.5, color = "#fff", className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

const Lock = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9AA3B5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.lock} aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V8a4 4 0 018 0v3" />
  </svg>
);

const INSTR_ICONS = {
  bell: (
    <>
      <path d="M8.7 3.9A6 6 0 0118 9c0 3.3.8 5.4 1.6 6.7M6 9c0 6-3 8-3 8h13" />
      <path d="M10.3 20.5a2 2 0 003.4 0" />
      <path d="M3 3l18 18" />
    </>
  ),
  door: (
    <>
      <path d="M6 21V4a1 1 0 011-1h10a1 1 0 011 1v17M3.5 21h17" />
      <path d="M14.5 12v.01" />
    </>
  ),
  guard: (
    <>
      <path d="M12 3l7 3v5c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6z" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
  phone: (
    <>
      <path d="M9.6 14.4A15 15 0 016.8 10l1.8-1.8a1 1 0 00.2-1.1 10.5 10.5 0 01-.6-3.4 1 1 0 00-1-1H4a1 1 0 00-1 1A17 17 0 007.4 15" />
      <path d="M12.4 17.1A17 17 0 0020 21a1 1 0 001-1v-3.2a1 1 0 00-1-1 10.5 10.5 0 01-3.4-.6 1 1 0 00-1.1.2l-1.4 1.4" />
      <path d="M3 3l18 18" />
    </>
  ),
};

const addressClasses = {
  root: styles.field,
  label: styles.label,
  input: styles.input,
  invalid: styles.invalid,
};

// The action the NDR / cancel API helpers send (verifyOtpAndAction).
const RESCHEDULE = { reschedule: true, cancel: false };
const CANCEL = { reschedule: false, cancel: true };

const isMasked = (value) => /^\*+$/.test(value || "");

// Form keys -> the error each one can cause (name / number / address).
const ERROR_FIELD = { name: "name", number: "number", address1: "address", address2: "address" };
const touchedFrom = (patch) =>
  Object.keys(patch || {}).reduce((acc, k) => (ERROR_FIELD[k] ? { ...acc, [ERROR_FIELD[k]]: true } : acc), {});

// The NDR API has one address field (newAddress). The page stores the flat
// details and the Google address in it as "<flat> | <Google address>", so the
// next load can split them back into the two fields. Addresses saved before
// this format have no separator and load fully into the Address field.
const FLAT_SEPARATOR = " | ";
const cleanFlat = (value) => (value ?? "").replace(/\|/g, "/");
const splitAddress = (full) => {
  const i = full.indexOf(FLAT_SEPARATOR);
  return i > 0 ? { address1: full.slice(0, i).trim(), address2: full.slice(i + FLAT_SEPARATOR.length).trim() } : { address1: "", address2: full };
};
const joinAddress = (flat, address) => [cleanFlat(flat).trim(), (address ?? "").trim()].filter(Boolean).join(FLAT_SEPARATOR);
// verifyOtpAndAction sends address2 as newAddress when address1 is empty.
const toApiFields = (form) => ({ ...form, address1: "", address2: joinAddress(form.address1, form.address2) });

// Fields the page reads from the order (logistic /cod/details/tracking_page via full-details).
const serverFields = (trackdata) => {
  const shipping = trackdata?.lastestAddress?.shippingAddress || {};
  const order = (Array.isArray(trackdata?.orderDetail) && trackdata.orderDetail[0]?.order) || {};
  return {
    name: shipping.name ?? "",
    number: shipping.msisdn ?? "",
    ...splitAddress(`${shipping.address ?? ""} ${shipping.address1 ?? ""}`.trim()),
    pincode: shipping.pinCode ?? "",
    city: shipping.city ?? "",
    state: shipping.state ?? "",
    lat: order.shipping_latitude ?? null,
    lng: order.shipping_longitude ?? null,
  };
};

const FaildedDelivered = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const param = useParams();
  const awb = param.awbInfo;
  // A saved OTP session belongs to one AWB: drop it when this page is for another one.
  ensureOtpSessionFor(awb);
  const [searchParams] = useSearchParams();
  usePageView("manage_order", { awb });
  const changed = useRef(new Set()); // field:choice pairs already sent as tp_manage_changed
  const viewed = useRef(false);

  const trackdata = useSelector((store) => store?.awbReducer?.data);
  const cancelationDisabledOrgIds = useSelector((store) => store?.configsReducer?.cancelationDisabledOrgIds) ?? [];

  const [draft] = useState(() => readStored(draftKey(awb)) || {});
  const [loadState, setLoadState] = useState("loading"); // loading | ok | error
  // True once this page has the order details it needs: from its own request, or from the
  // copy the OTP modal / an earlier load saved for this AWB. The store alone is not enough:
  // it can still hold the tracking page's order, which has no customer details.
  const [detailsReady, setDetailsReady] = useState(false);
  // Field errors show only for fields the buyer changed, or after a Submit attempt.
  const [touched, setTouched] = useState(() => touchedFrom(draft.edits));
  const [submitTried, setSubmitTried] = useState(false);
  // "Cancel" on the tracking page opens this page with ?isCancelling=true.
  // No tabs ("Manage Order.dc.html"): the cancel card sits under the form. "flow" only tells
  // the OTP modal which action asked for the OTP.
  const [flow, setFlow] = useState("manage");
  const cancelEntry = searchParams.get("isCancelling") === "true";
  const cancelRef = useRef(null);
  const [displayOtpPage, setotpPage] = useState(false);
  const [saved, setSaved] = useState(!!draft.saved);
  const [busy, setBusy] = useState(false);
  const [cancelStep, setCancelStep] = useState(draft.cancelDone ? "done" : "idle"); // idle | confirm | busy | done
  const [generalErrorModal, setGeneralErrorModal] = useState(false);
  const [generalError, setGeneralError] = useState("");
  const [isAddressFromGoogle, setIsAddressFromGoogle] = useState(true);
  const [addressFocused, setAddressFocused] = useState(false);
  const [enablePinCodeField, setEnablePinCodeField] = useState(!!draft.enablePinCodeField);
  const [pincodevalidity, setpincodevalidity] = useState(false);
  const [fakeattempt, setfakeattempt] = useState(false);
  // "" | Tomorrow | DayAfterTomorrow. A draft saved when a custom date existed is dropped.
  const [dateKey, setDateKey] = useState(DATE_KEYS.includes(draft.dateKey) ? draft.dateKey : "");
  const [slot, setSlot] = useState(draft.slot || "");
  const [instr, setInstr] = useState(draft.instr || {});
  const [note, setNote] = useState(draft.note || "");
  // Only the fields the buyer changed. They sit on top of the order data, so a
  // later load of the order never overwrites what the buyer typed.
  const [edits, setEdits] = useState(draft.edits || {});

  const shipment = trackdata?.shipmentDetails;
  const shipmentstatus = shipment?.shipmentstatus;
  const order = Array.isArray(trackdata?.orderDetail) ? trackdata.orderDetail[0]?.order : undefined;
  const ready = !!shipment && detailsReady;
  const hasSession = !!sessionStorage.getItem("validateotp");

  // Cancel is allowed only for a loaded order that has been picked up and whose
  // brand has not turned cancellation off. Unknown data never counts as allowed.
  const isCancellableOrder =
    ready &&
    !PRE_PICKUP_STATUSES.includes(shipmentstatus) &&
    !cancelationDisabledOrgIds.includes(trackdata?.companyInfo?.orgID);
  const activeTab = cancelEntry && isCancellableOrder ? "cancel" : "manage"; // entry focus, for analytics

  // "Cancel" on the tracking page (?isCancelling=true): bring the cancel card into view. Wait for
  // the cards' entry animation (0.5 s); a smooth scroll started while the page is still laying
  // out stops part of the way.
  useEffect(() => {
    if (!ready || !cancelEntry || !isCancellableOrder) return undefined;
    const t = setTimeout(() => cancelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 550);
    return () => clearTimeout(t);
  }, [ready, cancelEntry, isCancellableOrder]);

  // Funnel: the form is on screen (once per page load).
  useEffect(() => {
    if (!ready || viewed.current) return;
    viewed.current = true;
    const entry = searchParams.get("from") === "alert" ? "alert" : searchParams.get("isCancelling") === "true" ? "cancel_button" : "edit_button";
    track("tp_manage_viewed", { tab: activeTab, entry, cancel_allowed: isCancellableOrder, saved_before: saved });
    if (activeTab === "cancel") track("tp_cancel_flow", { step: "opened", via: entry });
  }, [ready, activeTab, isCancellableOrder, searchParams, saved]);

  const getAwb = useCallback(async () => {
    const withOtp = !!sessionStorage.getItem("validateotp");
    const cached = withOtp ? readStored(dataKey(awb)) : null;
    if (cached?.result) {
      // Show the saved details at once; the request below refreshes them.
      dispatch(setCompanyInfo(cached.result.companyInfo));
      dispatch(getAWBSuccess("data", cached.result));
      setLoadState("ok");
      setDetailsReady(true);
    } else {
      setLoadState("loading");
      setDetailsReady(false);
      dispatch(initiateApiCall());
    }

    const data = withOtp ? await verifyOtpActionForEdit(JSON.parse(sessionStorage.getItem("otp")), awb) : await searchAwb(awb);

    if (data?.code === "200") {
      dispatch(setCompanyInfo(data?.result?.companyInfo));
      dispatch(getAWBSuccess("data", data?.result));
      if (withOtp) writeStored(dataKey(awb), { result: data.result });
      setLoadState("ok");
      setDetailsReady(true);
    } else if (cached?.result) {
      setLoadState("ok");
      toast.info("Showing the last loaded order details.", optionsToast);
    } else {
      dispatch(getAWBFailure());
      setLoadState("error");
      toast.error("Something went wrong!!", optionsToast);
    }
  }, [awb, dispatch]);

  useEffect(() => {
    getAwb();
  }, [getAwb]);

  // Without an OTP session the order comes without the address. After the buyer
  // enters the OTP in the modal, load the full details once.
  const loadedWithOtp = useRef(hasSession);
  useEffect(() => {
    if (!displayOtpPage && !loadedWithOtp.current && sessionStorage.getItem("validateotp")) {
      loadedWithOtp.current = true;
      getAwb();
    }
  }, [displayOtpPage, getAwb]);

  const isBLR = (shipment?.sortCode || "").includes("no_city_allowed");

  // ---- The form: order data + buyer edits + choices ----
  const server = useMemo(() => serverFields(trackdata), [trackdata]);
  const chipLabels = INSTRUCTIONS.filter(([k]) => instr[k]).map(([, l]) => l);
  const deliveryInstruction = [...chipLabels, note.trim()].filter(Boolean).join(", ");
  const reschedule = useMemo(() => {
    const f = { address1: "", ...server, ...edits };
    // Tomorrow / Day after are always counted from today, also after a reload.
    const date =
      dateKey === "Tomorrow" ? day(1).toDate()
      : dateKey === "DayAfterTomorrow" ? day(2).toDate()
      : "";
    return { ...f, address: [f.address1, f.address2].filter(Boolean).join(", "), date, time: slot, deliveryInstruction };
  }, [server, edits, dateKey, slot, deliveryInstruction]);

  useEffect(() => {
    writeStored(draftKey(awb), { edits, dateKey, slot, instr, note, enablePinCodeField, saved, cancelDone: cancelStep === "done" });
  }, [awb, edits, dateKey, slot, instr, note, enablePinCodeField, saved, cancelStep]);

  // ---- Validation (from the values, so data loaded from the server is checked too) ----
  // The phone comes masked ("****"). Untouched, it is valid and is not sent.
  const phone = String(reschedule.number ?? "").trim();
  const errors = {
    name: !String(reschedule.name ?? "").trim() ? "Required" : null,
    number: !phone ? "Required" : phone === server.number && isMasked(phone) ? null : !/^\d{10}$/.test(phone) ? "Enter a 10-digit phone number" : null,
    address: !String(reschedule.address2 ?? "").trim() ? "Required" : !isAddressFromGoogle ? "Pick an address from the Google list, or undo your change. Add flat or floor details below." : null,
  };
  // Without an OTP session the address is hidden, so Submit must stay usable: it opens the OTP step.
  const formValid = !hasSession || (!errors.name && !errors.number && !errors.address);

  // ---- Field handlers ----
  // tp_manage_changed once per field (and choice) per page load.
  const noteChange = useCallback((field, extra = {}) => {
    const key = `${field}:${extra.choice ?? ""}`;
    if (!field || changed.current.has(key)) return;
    changed.current.add(key);
    track("tp_manage_changed", { field, ...extra });
  }, []);
  const edit = useCallback(
    (patch) => {
      setSaved(false);
      Object.keys(patch).forEach((k) => noteChange(FIELD_NAMES[k]));
      setEdits((prev) => ({ ...prev, ...patch }));
      setTouched((prev) => ({ ...prev, ...touchedFrom(patch) }));
    },
    [noteChange]
  );
  const onField = (event) => {
    const { id, value } = event.target;
    if (id === "address2") noteChange("address", { choice: "typed" });
    edit({ [id]: id === "address1" ? cleanFlat(value) : value });
  };
  const onPhoneFocus = (event) => {
    if (isMasked(event.target.value)) event.target.select();
  };

  const setNewCity = useCallback((city) => edit({ city }), [edit]);
  const setNewState = useCallback((state) => edit({ state }), [edit]);
  const setFormattedAddress = useCallback(
    (address2) => {
      noteChange("address", { choice: "google" });
      edit({ address2 });
    },
    [edit, noteChange]
  );
  const setLocation = useCallback(({ lat, lng }) => setEdits((prev) => ({ ...prev, lat, lng })), []);
  const setPinFromGoogleCode = useCallback(
    (pincode) => {
      if (pincode?.length === 6) {
        setEnablePinCodeField(false);
        edit({ pincode });
      } else {
        edit({ pincode: "" });
        setEnablePinCodeField(true);
      }
    },
    [edit]
  );

  const pickDay = (key) => {
    setSaved(false);
    noteChange("date", { choice: key === "Tomorrow" ? "tomorrow" : "day_after" });
    setDateKey(key);
  };
  const pickSlot = (time) => {
    setSaved(false);
    noteChange("slot", { choice: time });
    setSlot(time);
  };
  const toggleInstr = (key) => {
    setSaved(false);
    if (!instr[key]) noteChange("instruction", { choice: key });
    setInstr((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ---- Actions ----
  // Sends the OTP SMS and opens the modal. `trigger` names the button that needed it.
  const requestOtp = async (trigger) => {
    const data = await getOTP(shipment?.awbnumber);
    if (data?.status === "Success") {
      track("tp_otp_prompted", { trigger, prior_session: "none" });
      setotpPage(true);
    } else {
      trackError("otp_send", data, { trigger });
      toast.error("Something went wrong!!", optionsToast);
    }
  };

  // What the buyer changed, in the names operations use (tp_manage_submit.changed_fields).
  const changedFields = () => [
    ...Object.keys(edits).filter((k) => FIELD_NAMES[k] && (edits[k] ?? "") !== (server[k] ?? "")).map((k) => FIELD_NAMES[k]),
    ...(edits.address2 !== undefined && (edits.address2 ?? "") !== (server.address2 ?? "") ? ["address"] : []),
    ...(dateKey ? ["date"] : []),
    ...(slot ? ["slot"] : []),
    ...(chipLabels.length ? ["instruction"] : []),
    ...(note.trim() ? ["note"] : []),
  ];

  const handleSubmit = async () => {
    if (!sessionStorage.getItem("validateotp")) {
      setFlow("manage");
      return requestOtp("manage_submit");
    }
    if (!formValid || saved) {
      if (!formValid) setSubmitTried(true);
      track("tp_manage_submit", { result: "blocked", ndr_action: "reattempt_request", blocked_by: Object.keys(errors).filter((k) => errors[k]) });
      return;
    }
    setfakeattempt(shipmentstatus === "Failed Delivered");
    const payload = toApiFields(reschedule);
    if (!/^\d{10}$/.test(phone)) delete payload.number;
    const fields = changedFields();
    const elapsed = timer();

    setBusy(true);
    const response = await verifyOtpAndAction(
      payload,
      JSON.parse(sessionStorage.getItem("otp")),
      RESCHEDULE,
      awb,
      order,
      moment,
      trackdata?.orderDetail?.[0]?.suborder_id,
      setotpPage,
      fakeattempt
    );
    setBusy(false);
    const ok = response?.code === "200";
    track("tp_manage_submit", {
      result: ok ? "success" : "failed",
      ndr_action: "reattempt_request",
      changed_fields: fields,
      fields_count: fields.length,
      instruction_codes: INSTRUCTIONS.filter(([k]) => instr[k]).map(([k]) => k),
      has_note: !!note.trim(),
      elapsed_ms: elapsed(),
      code: ok ? undefined : errorCode(response),
    });
    if (ok) {
      setSaved(true);
    } else {
      setpincodevalidity(false);
      setGeneralError(response?.status || "Something went wrong. Please try again.");
      setGeneralErrorModal(true);
    }
  };

  const doCancel = async () => {
    if (!sessionStorage.getItem("validateotp")) {
      setFlow("cancel");
      return requestOtp("cancel_confirm");
    }
    track("tp_cancel_flow", { step: "confirmed" });
    const elapsed = timer();
    setCancelStep("busy");
    const data = await verifyOtpAndAction(
      reschedule,
      JSON.parse(sessionStorage.getItem("otp")),
      CANCEL,
      awb,
      order,
      moment,
      trackdata?.orderDetail?.[0]?.suborder_id,
      setotpPage
    );
    if (data?.code === "200") {
      track("tp_cancel_flow", { step: "success", elapsed_ms: elapsed() });
      setCancelStep("done");
    } else {
      track("tp_cancel_flow", { step: "failed", code: errorCode(data), elapsed_ms: elapsed() });
      setCancelStep("confirm");
      toast.error(data?.status || "Something went wrong!!", optionsToast);
    }
  };

  const handleReportClick = () => {
    track("tp_cta_click", { cta: "report", placement: "manage_card" });
    const handleReport = () =>
      verifyOtpAndAction(
        toApiFields(reschedule),
        JSON.parse(sessionStorage.getItem("otp")),
        RESCHEDULE,
        awb,
        order,
        moment,
        trackdata?.orderDetail?.[0]?.suborder_id,
        setotpPage,
        fakeattempt
      );
    navigate(`/${awb}/report`, { state: { handleReport } });
  };

  // ---- View values ----
  const selected = dateKey ? moment(reschedule.date) : null;
  const fieldsChanged = Object.keys(edits).some((k) => k !== "lat" && k !== "lng" && (edits[k] ?? "") !== (server[k] ?? ""));
  const dirty = !!selected || chipLabels.length > 0 || !!note.trim() || fieldsChanged;
  const cancelled = cancelStep === "done";
  const chip = cancelled ? "Cancelled" : saved ? "Saved" : dirty ? "Unsaved changes" : "No changes";
  const place = [[reschedule.city, reschedule.state].filter(Boolean).join(", "), reschedule.pincode].filter(Boolean).join(" ");
  const sumAddr = [reschedule.address1?.trim(), reschedule.address2?.trim()].filter(Boolean).join(", ");
  const orderRef = order?.shop_order_number || awb;
  // Not disabled when the form is invalid: a press shows which fields need attention.
  const submitDisabled = busy || saved;
  // Field errors show only when the form can be checked (OTP session, order loaded).
  const err = (k) => (hasSession && ready && (touched[k] || submitTried) ? errors[k] : null);
  // While the buyer types, Google may still be loading suggestions: hold the
  // "pick from the list" message until the field loses focus ("Required" shows at once).
  const addressError = err("address") && (!addressFocused || !String(reschedule.address2 ?? "").trim()) ? err("address") : null;
  const showReport = ready && !sessionStorage.getItem("validatereport") && shipmentstatus === "Failed Delivered";
  const dateOptions = [
    ["Tomorrow", "Tomorrow", 1],
    ["DayAfterTomorrow", "Day after tomorrow", 2],
  ];

  return (
    <div className={styles.shell}>
      <PageHeader forceLogo={loadState === "error"} chat />

      <main className={styles.main}>
        <div className={styles.inner}>
          {showReport && (
            <div className={styles.report}>
              <span className={styles.reportIcon}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                  <path d="M12 7v6" />
                  <path d="M12 17h.01" />
                </svg>
              </span>
              <span className={styles.reportText}>Report wrong status update or delivery partner misbehaviour.</span>
              <button type="button" className={styles.reportBtn} onClick={handleReportClick}>
                Report
              </button>
            </div>
          )}

          <div className={styles.topRow}>
            <button type="button" className={styles.back} aria-label="Back to tracking" onClick={() => navigate(`/${awb}`)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M15 6l-6 6 6 6" />
              </svg>
            </button>
            {ready ? (
              <div className={styles.titleBlock}>
                <h1 className={styles.pageTitle}>Manage Order</h1>
                <span className={styles.sub}>{order?.shop_order_number ? `Order ID ${order.shop_order_number}` : `AWB ${awb}`}</span>
              </div>
            ) : (
              loadState === "loading" && <div className={cx(styles.skel, styles.skelTitle)} aria-hidden="true" />
            )}
          </div>

          {!ready && loadState === "loading" && (
            <div className={styles.grid} aria-busy="true" aria-label="Loading your order">
              <div className={cx(styles.skel, styles.skelPanel)} />
              <div className={cx(styles.skel, styles.skelCard)} />
            </div>
          )}

          {!ready && loadState === "error" && (
            <div className={styles.errorCard}>
              <h2 className={styles.h2}>We couldn't load your order</h2>
              <span className={styles.sub}>Check your connection and try again.</span>
              <button type="button" className={styles.cBtn} onClick={getAwb}>
                Try again
              </button>
            </div>
          )}

          {ready && (
            <div className={styles.grid}>
              <div className={styles.panel}>
                <div className={styles.panelHead}>
                  <span className={styles.eyebrow}>Your delivery plan</span>
                  <span className={cx(styles.chip, cancelled ? styles.chipCancelled : saved ? styles.chipSaved : dirty && styles.chipDirty)}>
                    <span className={styles.chipDot} />
                    {chip}
                  </span>
                </div>

                <PlanScene arriving={selected ? selected.format("ddd, D MMM") : "As scheduled"} />

                <div className={styles.summary}>
                  <div className={styles.sumRow}>
                    <span className={styles.sumIcon}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 21s-7-6.2-7-11.5A7 7 0 0112 2.5a7 7 0 017 7C19 14.8 12 21 12 21z" />
                        <circle cx="12" cy="9.5" r="2.5" />
                      </svg>
                    </span>
                    <div className={styles.sumText}>
                      <span className={styles.sumName}>{reschedule.name || "—"}</span>
                      <span className={styles.sumAddr}>{sumAddr || "—"}</span>
                      {place && <span className={styles.sumPlace}>{place}</span>}
                    </div>
                  </div>
                  <div className={styles.sumInstr}>
                    <span className={styles.sumLabel}>Instructions for your Kaptaan</span>
                    {!chipLabels.length && !note.trim() && <span className={styles.sumNone}>None added yet</span>}
                    {chipLabels.length > 0 && (
                      <div className={styles.sumChips}>
                        {chipLabels.map((c) => (
                          <span key={c} className={styles.sumChip}>
                            {c}
                          </span>
                        ))}
                      </div>
                    )}
                    {!!note.trim() && <span className={styles.sumNote}>“{note.trim()}”</span>}
                  </div>
                </div>
              </div>

              <div className={styles.column}>
              <div className={cx(styles.card, cancelled && styles.cardDone)} aria-disabled={cancelled || undefined}>
                <section className={cx(styles.section, styles.sectionWide)}>
                  <h2 className={styles.h2}>Customer Details</h2>
                  <div className={styles.row2}>
                    <label className={styles.field}>
                      <span className={styles.label}>Name</span>
                      <input id="name" value={reschedule.name ?? ""} onChange={onField} placeholder="Enter Name" aria-invalid={!!err("name")} className={cx(styles.input, err("name") && styles.invalid)} />
                      {err("name") && <span className={styles.error}>{err("name")}</span>}
                    </label>
                    <label className={styles.field}>
                      <span className={styles.label}>Phone Number</span>
                      <input id="number" value={reschedule.number ?? ""} onChange={onField} onFocus={onPhoneFocus} inputMode="tel" placeholder="Phone Number" aria-invalid={!!err("number")} className={cx(styles.input, styles.tel, err("number") && styles.invalid)} />
                      {err("number") && <span className={styles.error}>{err("number")}</span>}
                    </label>
                  </div>
                  <div onFocus={() => setAddressFocused(true)} onBlur={() => setAddressFocused(false)}>
                  <SearchLocationInput
                    classes={addressClasses}
                    labelText="Address"
                    placeholderText="Area/Street/Sector"
                    isRedInputBorder={!!addressError}
                    setIsAddressFromGoogle={setIsAddressFromGoogle}
                    address2={reschedule.address2 ?? ""}
                    setLocation={setLocation}
                    setFormattedAddress={setFormattedAddress}
                    awb={awb}
                    setNewCity={setNewCity}
                    setNewState={setNewState}
                    setPin={setPinFromGoogleCode}
                  />
                  </div>
                  {addressError && <span className={styles.error}>{addressError}</span>}
                  <label className={styles.field}>
                    <span className={styles.label}>Flat no / House no / Floor</span>
                    <input id="address1" value={reschedule.address1 ?? ""} onChange={onField} placeholder="Flat no / House no / Floor" className={styles.input} />
                  </label>
                  <div className={styles.lockedBlock}>
                    <div className={styles.row3}>
                      <label className={styles.field}>
                        <span className={styles.label}>Pincode</span>
                        <div className={styles.lockWrap}>
                          <input
                            id="pincode"
                            value={reschedule.pincode ?? ""}
                            onChange={onField}
                            disabled={!enablePinCodeField}
                            inputMode="numeric"
                            placeholder="Enter Pincode"
                            className={cx(styles.input, !enablePinCodeField && styles.locked)}
                          />
                          {!enablePinCodeField && <Lock />}
                        </div>
                        {pincodevalidity && <span className={styles.error}>Pincode out of serviceable area</span>}
                      </label>
                      <label className={styles.field}>
                        <span className={styles.label}>City</span>
                        <div className={styles.lockWrap}>
                          <input value={reschedule.city ?? ""} disabled readOnly placeholder="City" className={cx(styles.input, styles.locked)} />
                          <Lock />
                        </div>
                      </label>
                      <label className={styles.field}>
                        <span className={styles.label}>State</span>
                        <div className={styles.lockWrap}>
                          <input value={reschedule.state ?? ""} disabled readOnly placeholder="State" className={cx(styles.input, styles.locked)} />
                          <Lock />
                        </div>
                      </label>
                    </div>
                    <span className={styles.hint}>Pincode, city and state are filled in from the address you select.</span>
                  </div>
                </section>

                <div className={styles.rule} />

                <section className={styles.section}>
                  <div className={styles.titleBlock}>
                    <h2 className={styles.h2}>Reschedule Delivery</h2>
                    <span className={styles.sub}>Select Date</span>
                  </div>
                  <div className={styles.options}>
                    {dateOptions.map(([key, label, offset]) => (
                      <button key={key} type="button" className={cx(styles.option, dateKey === key && styles.optionOn)} onClick={() => pickDay(key)} aria-pressed={dateKey === key}>
                        <span className={styles.optSub}>{label}</span>
                        <span className={styles.optMain}>{day(offset).format("D MMM")}</span>
                        <span className={styles.optDot}>
                          <Check />
                        </span>
                      </button>
                    ))}
                  </div>
                  {isBLR && (
                    <>
                      <span className={styles.sub}>Preferred time slot</span>
                      <div className={styles.options}>
                        {TIME_SLOTS.map((time) => (
                          <button key={time} type="button" className={cx(styles.option, slot === time && styles.optionOn)} onClick={() => pickSlot(time)} aria-pressed={slot === time}>
                            <span className={styles.optSub}>Time slot</span>
                            <span className={styles.optMain}>{time}</span>
                            <span className={styles.optDot}>
                              <Check />
                            </span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </section>

                <div className={styles.rule} />

                <section className={styles.section}>
                  <h2 className={styles.h2}>Add Instruction for delivery person</h2>
                  <div className={styles.instrGrid}>
                    {INSTRUCTIONS.map(([key, label]) => (
                      <button key={key} type="button" aria-pressed={!!instr[key]} className={cx(styles.instr, instr[key] && styles.instrOn)} onClick={() => toggleInstr(key)}>
                        <span className={styles.instrIcon}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            {INSTR_ICONS[key]}
                          </svg>
                        </span>
                        <span className={styles.instrLabel}>{label}</span>
                        {instr[key] && <Check size={16} width={2.6} color="#7130F0" className={styles.instrCheck} />}
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={note}
                    onChange={(e) => {
                      setSaved(false);
                      noteChange("note");
                      setNote(e.target.value);
                    }}
                    placeholder="ex: give to security"
                    rows={4}
                    className={styles.textarea}
                  />
                </section>

                <div className={styles.actions}>
                  <button type="button" className={cx(styles.submit, saved && styles.submitSaved)} onClick={handleSubmit} disabled={submitDisabled}>
                    {saved && <Check size={18} width={2.6} className={styles.pop} />}
                    {saved ? "Changes saved" : busy ? "Saving…" : "Submit"}
                  </button>
                </div>
              </div>

              {isCancellableOrder && (
                <div ref={cancelRef} className={styles.cancelCard}>
                  <span className={cx(styles.cIcon, cancelStep === "done" && styles.cIconDone)}>
                    {cancelStep === "done" ? (
                      <Check size={24} width={2.4} color="currentColor" className={styles.pop} />
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M21 8l-9-5-9 5 9 5 9-5z" />
                        <path d="M3 8v8l9 5 9-5V8" />
                        <path d="M14.5 13.5l4 4M18.5 13.5l-4 4" />
                      </svg>
                    )}
                  </span>
                  <div className={styles.cBody}>
                    {cancelStep !== "done" && (
                      <div className={styles.cHead}>
                        <h2 className={styles.cEyebrow}>Cancel your order</h2>
                        <p className={styles.cText}>If you no longer wish to receive your order, click below to cancel your order.</p>
                      </div>
                    )}
                    {cancelStep === "idle" && (
                      <button
                        type="button"
                        className={styles.cBtn}
                        onClick={() => {
                          track("tp_cancel_flow", { step: "confirm_shown" });
                          setCancelStep("confirm");
                        }}
                      >
                        Cancel
                      </button>
                    )}
                    {(cancelStep === "confirm" || cancelStep === "busy") && (
                      <div className={styles.confirm}>
                        <span className={styles.confirmQ}>Cancel order {orderRef}?</span>
                        <div className={styles.confirmBtns}>
                          <button type="button" className={styles.yes} onClick={doCancel} disabled={cancelStep === "busy"}>
                            {cancelStep === "busy" ? "Cancelling…" : "Yes, cancel order"}
                          </button>
                          <button
                            type="button"
                            className={styles.keep}
                            onClick={() => {
                              track("tp_cancel_flow", { step: "kept" });
                              setCancelStep("idle");
                            }}
                            disabled={cancelStep === "busy"}
                          >
                            Keep my order
                          </button>
                        </div>
                      </div>
                    )}
                    {cancelStep === "done" && (
                      <div className={styles.done}>
                        <h2 className={styles.doneTitle}>Order cancelled</h2>
                        <p className={styles.cText}>Order {orderRef} has been cancelled.</p>
                        <button type="button" className={styles.link} onClick={() => navigate(`/${awb}`)}>
                          Back to tracking
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div>
            </div>
          )}
        </div>
      </main>

      {displayOtpPage && (
        <Otp
          status={flow === "cancel" ? "cancel" : "reschedule"}
          setActivestatus={(st) => setFlow(st?.cancel ? "cancel" : "manage")}
          setItemListModal={() => {}}
          action={"Edit"}
          setotpPage={setotpPage}
          subOrderId={trackdata?.data?.orderDetail[0]?.suborder_id}
          orderInfo={trackdata?.data?.orderDetail[0]?.order}
          awb={shipment?.awbnumber}
          activeStatus={flow !== "cancel"}
          reschedule={toApiFields(reschedule)}
          close={() => setotpPage(false)}
          setsuccessmessagemodal={(v) => v && setSaved(true)}
          setcancelmessagemodal={(v) => v && setCancelStep("done")}
        />
      )}

      {generalErrorModal && (
        <ModalForm
          isOpen
          width={window.screen.availWidth < 600 ? "100%" : 350}
          dimensionsUnit={"px"}
          background={"#0A2A6B"}
          noNavbar={true}
          withCloseIcon={false}
          close={() => setGeneralErrorModal(false)}
          onClose={() => setGeneralErrorModal(false)}
          renderContent={() => <GeneralError title="Error Occurred" description={generalError} />}
        ></ModalForm>
      )}
    </div>
  );
};

export default FaildedDelivered;
